import React from "react";
import { Link as RouterLink } from "lucide-react";

const ANSWERS = [
  {
    q: "How much do curtains cost in Dubai?",
    a: "Curtain pricing depends on fabric, size, and design. Basic curtains start from affordable ranges, while premium and motorized systems vary based on specifications.",
  },
  {
    q: "What is the best curtain style for apartments in Dubai?",
    a: "Ripple fold and sheer curtains are popular for modern apartments, while blackout curtains are ideal for bedrooms.",
  },
  {
    q: "Do you provide motorized curtains?",
    a: "Yes. Time Furniture provides motorized curtain systems with remote and smart home integration using premium automation solutions.",
  },
  {
    q: "How long does curtain installation take?",
    a: "Standard curtain installations are completed within a few days after measurement and fabrication.",
  },
  {
    q: "Do you provide measurement and installation?",
    a: "Yes. We provide complete measurement, supply, and installation services across Dubai and Sharjah.",
  },
];

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export default function AnswersSection({
  testid = "answers-section",
  eyebrow = "Answers",
  heading = "Curtain questions, answered.",
  withSchema = false,
  pageUrl = "https://timefurniture.ae/curtains-dubai",
}) {
  const items = ANSWERS.map((a) => ({ ...a, id: slugify(a.q) }));

  return (
    <section
      data-testid={testid}
      className="bg-[#FAF5EC] border-y border-[#6E1F2C]/15"
    >
      {withSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: items.map((a) => ({
                "@type": "Question",
                name: a.q,
                url: `${pageUrl}#${a.id}`,
                acceptedAnswer: { "@type": "Answer", text: a.a },
              })),
            }),
          }}
        />
      )}

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 lg:py-40">
        <div className="max-w-3xl mb-14 md:mb-20">
          <p className="eyebrow mb-6">{eyebrow}</p>
          <h2 className="font-serif-display text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C110C]">
            {heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-12 divide-y divide-[#6E1F2C]/15">
            {items.map((item, i) => (
              <div
                key={item.id}
                id={item.id}
                data-testid={`${testid}-item-${i}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 py-10 md:py-12 first:pt-0 scroll-mt-28"
              >
                <div className="md:col-span-5">
                  <h2
                    data-testid={`${testid}-q-${i}`}
                    className="font-serif-display text-xl md:text-2xl lg:text-[26px] leading-[1.25] text-[#1C110C] flex items-start gap-3"
                  >
                    <span>{item.q}</span>
                    <a
                      href={`#${item.id}`}
                      data-testid={`${testid}-anchor-${i}`}
                      aria-label={`Anchor link to: ${item.q}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[#6E1F2C] mt-2 shrink-0"
                      onClick={(e) => {
                        // Update URL without page jump; let browser handle scroll
                        e.stopPropagation();
                      }}
                    >
                      <RouterLink size={16} strokeWidth={2} />
                    </a>
                  </h2>
                </div>
                <p
                  data-testid={`${testid}-a-${i}`}
                  className="md:col-span-7 text-[#3E2A22] leading-[1.85] text-base md:text-[17px]"
                >
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
